import React, { useEffect, useState, useMemo, useRef } from "react";
import { apiClient } from "../../apiclient/apiclient";
import Loader from "../../components/Loader";
import PageContainer from "../../components/PageContainer";
import ThemedTable from "../../components/ThemedTable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays } from "date-fns";
import { Download, Calendar, RotateCcw, Search } from "lucide-react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder,
  labelKey = "name",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const selected = options.find((o) => o._id === value);
    if (selected && selected._id !== "all") {
      setQuery(
        `${selected[labelKey]} ${selected.phone ? `(${selected.phone})` : ""}`
      );
    } else {
      setQuery("");
    }
  }, [value, options]);

  const filtered = options.filter((o) =>
    `${o[labelKey]} ${o.phone || ""}`
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={ref}>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full shadow-sm text-slate-700 font-semibold pr-8"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      <span 
        className="absolute right-3 top-2.5 text-slate-400 text-[10px] pointer-events-none cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        ▼
      </span>

      {open && (
        <ul className="absolute w-full bg-white border rounded mt-1 max-h-48 overflow-auto shadow z-50 divide-y divide-slate-50">
          {filtered.length === 0 && (
            <li className="p-2 text-sm text-gray-500">No results</li>
          )}

          {filtered.map((o) => (
            <li
              key={o._id}
              className="p-2 hover:bg-gray-100 cursor-pointer text-left text-slate-700 text-sm"
              onClick={() => {
                onChange(o._id);
                setQuery(`${o[labelKey]} ${o.phone ? `(${o.phone})` : ""}`);
                setOpen(false);
              }}
            >
              {o[labelKey]} {o.phone && `(${o.phone})`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const StaffSales = () => {
  const [fromDate, setFromDate] = useState(subDays(new Date(), 7));
  const [toDate, setToDate] = useState(new Date());
  const [selectedAgentFilter, setSelectedAgentFilter] = useState("all");
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("all");

  // Temporary filter states before clicking Search
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const [tempAgentFilter, setTempAgentFilter] = useState(selectedAgentFilter);
  const [tempBrandFilter, setTempBrandFilter] = useState("all");

  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // 1. Fetch Users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await apiClient.get("/all");
        setUsers(res.data?.users || []);
      } catch (err) {
        console.error("Failed to load staff list:", err);
      }
    };
    fetchUsers();
  }, []);

  // 2. Fetch Products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await apiClient.get("/products");
        setProducts(res.data || []);
      } catch (err) {
        console.error("Failed to load products list:", err);
      }
    };
    fetchProducts();
  }, []);

  // Create User ID -> Name map
  const userMap = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      if (u._id && u.name) {
        map[u._id] = u.name;
      }
    });
    return map;
  }, [users]);

  // Create staff options list for the searchable selector
  const staffOptions = useMemo(() => {
    return [
      { _id: "all", name: "All Agents / Sources" },
      ...users
        .filter((u) => u.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [users]);

  // Create Product ID -> Brand Name map
  const productBrandMap = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      if (p._id) {
        map[p._id] = p.brand?.name || "No Brand";
      }
    });
    return map;
  }, [products]);

  // Create unique Brand Options list for the selector
  const brandOptions = useMemo(() => {
    const unique = new Set();
    products.forEach((p) => {
      if (p.brand?.name) {
        unique.add(p.brand.name);
      }
    });
    return [
      { _id: "all", name: "All Brands" },
      ...Array.from(unique)
        .sort()
        .map((b) => ({ _id: b, name: b })),
    ];
  }, [products]);

  // 3. Fetch Orders
  const loadReportData = async (start = fromDate, end = toDate) => {
    if (!start || !end) {
      toast.error("Please select a valid date range");
      return;
    }

    try {
      setLoading(true);
      const startStr = format(start, "yyyy-MM-dd");
      const endStr = format(end, "yyyy-MM-dd");

      // Retrieve orders with status counts & high limit to capture all transactions
      const res = await apiClient.get(
        `/orders?limit=5000&from=${startStr}&to=${endStr}&status=completed`
      );
      setOrders(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load sales data:", err);
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData(fromDate, toDate);
  }, []);

  // 4. Perform Frontend Aggregation (Item-Level Reference Attribution)
  const aggregatedData = useMemo(() => {
    const groups = {};

    orders.forEach((order) => {
      const defaultRefId =
        typeof order.staffReference === "object" && order.staffReference !== null
          ? order.staffReference._id
          : order.staffReference;

      const items = order.items || [];
      items.forEach((item) => {
        // Determine staff reference for this specific item (with fallback to order reference)
        let itemRefId = null;
        if (item.staffReference) {
          itemRefId =
            typeof item.staffReference === "object" && item.staffReference !== null
              ? item.staffReference._id
              : item.staffReference;
        } else if (defaultRefId) {
          itemRefId = defaultRefId;
        }

        let agentName = "Direct Sales (No Agent)";
        if (itemRefId) {
          agentName = userMap[itemRefId] || "Unknown Staff";
        }

        const prodId = item.productId?._id || item.productId;
        const brandName = productBrandMap[prodId] || "Unknown Brand";
        const qty = Number(item.quantity || 0);

        // Calculate line total price
        const price = Number(item.price || 0);
        const total = price * qty;

        const key = `${agentName}_${brandName}`;
        if (!groups[key]) {
          groups[key] = {
            agentName,
            brandName,
            totalQty: 0,
            totalRevenue: 0,
          };
        }

        groups[key].totalQty += qty;
        groups[key].totalRevenue += total;
      });
    });

    // Sort by agentName first, then brandName
    return Object.values(groups).sort((a, b) => {
      const agentCompare = a.agentName.localeCompare(b.agentName);
      if (agentCompare !== 0) return agentCompare;
      return a.brandName.localeCompare(b.brandName);
    });
  }, [orders, userMap, productBrandMap]);

  // Filter aggregated data by selected agent & brand
  const filteredAggregatedData = useMemo(() => {
    let data = aggregatedData;

    // Apply Agent Filter
    if (selectedAgentFilter === "all") {
      // Exclude Direct Sales by default from "All Agents" view
      data = data.filter((row) => row.agentName !== "Direct Sales (No Agent)");
    } else {
      const targetAgentName = userMap[selectedAgentFilter] || "Unknown Staff";
      data = data.filter((row) => row.agentName === targetAgentName);
    }

    // Apply Brand Filter
    if (selectedBrandFilter !== "all") {
      data = data.filter((row) => row.brandName === selectedBrandFilter);
    }

    return data;
  }, [aggregatedData, selectedAgentFilter, selectedBrandFilter, userMap]);

  // 5. Calculate KPIs
  const kpis = useMemo(() => {
    let totalRefSales = 0;
    const agentTotals = {};
    const brandTotals = {};

    filteredAggregatedData.forEach((row) => {
      if (row.agentName !== "Direct Sales (No Agent)") {
        totalRefSales += row.totalRevenue;
      }
      agentTotals[row.agentName] = (agentTotals[row.agentName] || 0) + row.totalRevenue;
      brandTotals[row.brandName] = (brandTotals[row.brandName] || 0) + row.totalRevenue;
    });

    let topAgent = "N/A";
    let topAgentVal = 0;
    Object.entries(agentTotals).forEach(([name, val]) => {
      if (name !== "Direct Sales (No Agent)" && val > topAgentVal) {
        topAgent = name;
        topAgentVal = val;
      }
    });

    let topBrand = "N/A";
    let topBrandVal = 0;
    Object.entries(brandTotals).forEach(([name, val]) => {
      if (val > topBrandVal) {
        topBrand = name;
        topBrandVal = val;
      }
    });

    return {
      totalRefSales,
      topAgent:
        topAgent !== "N/A"
          ? `${topAgent} (₹${topAgentVal.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })})`
          : "N/A",
      topBrand:
        topBrand !== "N/A"
          ? `${topBrand} (₹${topBrandVal.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })})`
          : "N/A",
    };
  }, [filteredAggregatedData]);

  // 6. Excel Export Functionality
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Staff Brand Sales");

      sheet.mergeCells("A1:D1");
      const titleCell = sheet.getCell("A1");
      titleCell.value = "STAFF & BRAND WISE SALES REPORT";
      titleCell.font = { size: 14, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF172554" },
      };
      sheet.getRow(1).height = 30;

      sheet.mergeCells("A2:D2");
      const periodCell = sheet.getCell("A2");
      periodCell.value = `Period: ${format(fromDate, "dd/MM/yyyy")} to ${format(
        toDate,
        "dd/MM/yyyy"
      )}`;
      periodCell.font = { size: 10, italic: true, color: { argb: "FFFFFFFF" } };
      periodCell.alignment = { horizontal: "center", vertical: "middle" };
      periodCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF172554" },
      };
      sheet.getRow(2).height = 20;

      sheet.addRow([]); // Blank spacer

      const header = sheet.addRow([
        "Reference Agent",
        "Brand Name",
        "Total Quantity Sold",
        "Total Sales Revenue (₹)",
      ]);
      header.height = 24;
      header.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEA580C" }, // Orange-600
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      filteredAggregatedData.forEach((row) => {
        const added = sheet.addRow([
          row.agentName,
          row.brandName,
          row.totalQty,
          row.totalRevenue,
        ]);
        added.height = 20;
        added.getCell(3).alignment = { horizontal: "center" };
        added.getCell(4).alignment = { horizontal: "right" };
        added.getCell(4).numFormat = '"₹"#,##0.00';
      });

      sheet.columns.forEach((col) => {
        col.width = 25;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Staff_Wise_Sales_${format(fromDate, "dd-MM-yyyy")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate Excel report");
    } finally {
      setExporting(false);
    }
  };

  const handleResetFilters = () => {
    const defaultFrom = subDays(new Date(), 7);
    const defaultTo = new Date();
    setTempFromDate(defaultFrom);
    setTempToDate(defaultTo);
    setTempAgentFilter("all");
    setTempBrandFilter("all");

    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setSelectedAgentFilter("all");
    setSelectedBrandFilter("all");
    loadReportData(defaultFrom, defaultTo);
  };

  return (
    <PageContainer>
      <div className="w-full border-b pt-3 px-6 pb-4 border-slate-200 bg-blue-50">
        <h1 className="text-xl font-bold text-slate-800">Staff Wise Sales</h1>
        <p className="text-sm text-slate-500 mt-1">
          Monitor sales performance grouped by reference agent and product brands
        </p>
      </div>

      {/* KPI SUMMARIES */}
      <div className="grid grid-cols-1 md:grid-cols-3">
        {/* Total Staff Reference Sales */}
        <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-b md:border-e flex flex-col justify-between min-h-[115px]">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
          <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">
            Total Staff Reference Sales
          </p>
          <h2 className="text-xl md:text-2xl font-bold mt-1 relative z-10 whitespace-nowrap">
            ₹
            {kpis.totalRefSales.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </h2>
          <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">
            This Period
          </p>
        </div>

        {/* Top Performing Agent */}
        <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-700 border-b md:border-e flex flex-col justify-between min-h-[115px]">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
          <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">
            Top Performing Agent
          </p>
          <h2 className="text-lg md:text-xl font-bold mt-1 relative z-10 truncate" title={kpis.topAgent}>
            {kpis.topAgent}
          </h2>
          <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">
            This Period
          </p>
        </div>

        {/* Top Selling Brand */}
        <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-b flex flex-col justify-between min-h-[115px]">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
          <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">
            Top Selling Brand
          </p>
          <h2 className="text-lg md:text-xl font-bold mt-1 relative z-10 truncate" title={kpis.topBrand}>
            {kpis.topBrand}
          </h2>
          <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">
            This Period
          </p>
        </div>
      </div>

      {/* ================= CONTROL BAR (LIGHT BLUE SECTION) ================= */}
      <div className="border-t border-b bg-[#EFF6FF] p-3">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {/* FROM */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              From
            </label>
            <DatePicker
              selected={tempFromDate}
              onChange={(date) => setTempFromDate(date)}
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-40 shadow-sm font-semibold"
            />
          </div>

          {/* TO */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              To
            </label>
            <DatePicker
              selected={tempToDate}
              onChange={(date) => setTempToDate(date)}
              dateFormat="dd/MM/yyyy"
              minDate={tempFromDate}
              maxDate={new Date()}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-40 shadow-sm font-semibold"
            />
          </div>

          {/* AGENT SELECTOR */}
          <div className="flex items-center gap-2 w-full sm:flex-1">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              Agent
            </label>
            <div className="w-full">
              <SearchableSelect
                options={staffOptions}
                value={tempAgentFilter}
                onChange={(id) => setTempAgentFilter(id)}
                placeholder="Search staff reference..."
              />
            </div>
          </div>

          {/* BRAND SELECTOR */}
          <div className="flex items-center gap-2 w-full sm:flex-1">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              Brand
            </label>
            <div className="w-full">
              <SearchableSelect
                options={brandOptions}
                value={tempBrandFilter}
                onChange={(id) => setTempBrandFilter(id)}
                placeholder="Search brand..."
              />
            </div>
          </div>

          {/* ACTIONS */}
          <div className="flex gap-2 w-full sm:w-auto justify-end">
            {/* SEARCH BUTTON */}
            <button
              onClick={() => {
                setFromDate(tempFromDate);
                setToDate(tempToDate);
                setSelectedAgentFilter(tempAgentFilter);
                setSelectedBrandFilter(tempBrandFilter);
                loadReportData(tempFromDate, tempToDate);
              }}
              disabled={loading}
              className="w-9 h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
              title="View Report"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Search size={18} />
              )}
            </button>

            {/* RESET BUTTON */}
            <button
              onClick={handleResetFilters}
              className="w-9 h-9 border border-slate-200 bg-white text-slate-600 rounded-lg hover:bg-slate-50 transition-all shadow-sm flex items-center justify-center shrink-0"
              title="Reset Filters"
            >
              <RotateCcw size={16} />
            </button>

            {/* EXPORT BUTTON */}
            <button
              onClick={handleExportExcel}
              disabled={exporting || filteredAggregatedData.length === 0}
              className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
              title="Download Excel"
            >
              {exporting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Download size={18} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* DATA TABLE */}
      <div className="w-full">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader size={40} />
          </div>
        ) : filteredAggregatedData.length === 0 ? (
          <div className="p-6">
            <div className="text-center py-16 border bg-white border-dashed text-slate-400 text-sm">
              No completed sales found for the selected period.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200 bg-white">
            <ThemedTable className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
                  <th className="p-3 text-center w-10 hidden md:table-cell">#</th>
                  <th className="p-3 text-left">Reference Agent / Source</th>
                  <th className="p-3 text-left">Brand Name</th>
                  <th className="p-3 text-center">Total Quantity Sold</th>
                  <th className="p-3 text-right">Total Revenue Generated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredAggregatedData.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition bg-white"
                  >
                    <td className="px-2 py-3 text-center text-slate-400 font-medium hidden md:table-cell">
                      {idx + 1}
                    </td>
                    <td className="px-2 py-3 font-bold text-[#172554] whitespace-nowrap">
                      {row.agentName}
                    </td>
                    <td className="px-2 py-3 text-slate-600 whitespace-nowrap font-medium">
                      {row.brandName}
                    </td>
                    <td className="px-2 py-3 text-center whitespace-nowrap font-bold text-blue-900">
                      {row.totalQty}
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap font-bold text-emerald-600">
                      ₹
                      {row.totalRevenue.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ThemedTable>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default StaffSales;
